import type { RoomType } from '../../game/types';
import { shuffle } from './random';

export type RandomRoomType = Exclude<RoomType, 'SAFE' | 'BOSS' | 'TELEPORTER'>;

const WEIGHTS: Record<RandomRoomType, number> = {
  NORMAL_ENEMY: 32,
  ELITE_ENEMY: 9,
  TREASURE: 12,
  NPC: 13,
  TRAP: 10,
  SECRET: 5,
  EMPTY: 19,
};

const TYPES = Object.keys(WEIGHTS) as RandomRoomType[];
const DANGEROUS = new Set<RandomRoomType>(['NORMAL_ENEMY', 'ELITE_ENEMY', 'TRAP']);
const REWARD = new Set<RandomRoomType>(['TREASURE', 'SECRET']);
const OPPORTUNITY: RandomRoomType[] = ['TREASURE', 'NPC', 'EMPTY'];

export interface RoomBalanceLimits {
  maxByType: Record<RandomRoomType, number>;
  maxDangerous: number;
  maxReward: number;
}

export function roomBalanceLimits(slotCount: number): RoomBalanceLimits {
  return {
    maxByType: {
      NORMAL_ENEMY: Math.max(1, Math.ceil(slotCount * 0.4)),
      ELITE_ENEMY: slotCount >= 5 ? Math.max(1, Math.ceil(slotCount * 0.14)) : 0,
      TREASURE: Math.max(1, Math.ceil(slotCount * 0.2)),
      NPC: Math.max(1, Math.ceil(slotCount * 0.2)),
      TRAP: slotCount >= 4 ? Math.max(1, Math.ceil(slotCount * 0.15)) : 0,
      SECRET: slotCount >= 6 ? (slotCount >= 14 ? 2 : 1) : 0,
      EMPTY: Math.max(1, Math.ceil(slotCount * 0.3)),
    },
    maxDangerous: Math.ceil(slotCount * 0.55),
    maxReward: Math.ceil(slotCount * 0.25),
  };
}

function countWhere(deck: readonly RandomRoomType[], set: ReadonlySet<RandomRoomType>): number {
  return deck.filter((type) => set.has(type)).length;
}

function weightedPick(rng: () => number, candidates: readonly RandomRoomType[]): RandomRoomType {
  const total = candidates.reduce((sum, type) => sum + WEIGHTS[type], 0);
  let roll = rng() * total;
  for (const type of candidates) {
    roll -= WEIGHTS[type];
    if (roll < 0) {
      return type;
    }
  }
  return candidates[candidates.length - 1] ?? 'EMPTY';
}

/**
 * Randomized room deck constrained by per-type, danger and reward budgets.
 * Player stats never affect this distribution.
 */
export function buildBalancedRoomDeck(
  slotCount: number,
  rng: () => number,
): RandomRoomType[] {
  if (slotCount <= 0) {
    return [];
  }

  const limits = roomBalanceLimits(slotCount);
  const deck: RandomRoomType[] = [];

  if (slotCount >= 2) {
    deck.push('NORMAL_ENEMY');
  }
  if (slotCount >= 4) {
    deck.push(weightedPick(rng, OPPORTUNITY));
  }

  while (deck.length < slotCount) {
    const candidates = TYPES.filter((type) => {
      const typeCount = deck.filter((entry) => entry === type).length;
      if (typeCount >= limits.maxByType[type]) {
        return false;
      }
      if (DANGEROUS.has(type) && countWhere(deck, DANGEROUS) >= limits.maxDangerous) {
        return false;
      }
      if (REWARD.has(type) && countWhere(deck, REWARD) >= limits.maxReward) {
        return false;
      }
      return true;
    });

    deck.push(weightedPick(rng, candidates.length > 0 ? candidates : TYPES));
  }

  return shuffle(rng, deck);
}

export function isDangerousRoomType(type: RandomRoomType): boolean {
  return DANGEROUS.has(type);
}

export function isRewardRoomType(type: RandomRoomType): boolean {
  return REWARD.has(type);
}
